import { log } from '@/lib/log'
import { ApiError, NetworkError, type Problem } from './errors'

/**
 * The one way this app talks to the backend (best practices §1.4).
 *
 * Three things happen here and nowhere else:
 *
 *   1. **Credentials.** The session is httpOnly cookies the app cannot read, so every request
 *      sends `credentials: 'include'` and no caller ever handles a token.
 *   2. **CSRF.** Cookie-borne credentials are attachable by a cross-site form, so every unsafe
 *      method carries `X-CSRF-Token` read from the deliberately-readable `vecos_admin_csrf`
 *      cookie. The server compares in constant time.
 *   3. **Errors.** A non-2xx becomes an `ApiError` carrying the server's `code`, so callers
 *      branch on a stable value rather than on prose.
 */

/*
 * The whole API is versioned: `app.ts` registers everything inside `{ prefix: '/v1' }`, and the
 * contract declares `servers: [{ url: '/v1' }]` — which is why its paths look unversioned.
 * Calling `/admin/...` reaches nothing; verified against the running server, where `/v1/worlds`
 * answers 200 and `/worlds` answers 404.
 */
const BASE = '/v1/admin'
const CSRF_COOKIE = 'vecos_admin_csrf'
const CSRF_HEADER = 'X-CSRF-Token'
const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

export type QueryValue = string | number | boolean | null | undefined

/** What a blob request answers with — the bytes, and the name the server asked us to save under. */
export interface DownloadedFile {
  blob: Blob
  filename: string | null
}

/**
 * The filename from `Content-Disposition`, or null.
 *
 * Only the quoted `filename=` form is read, and anything with a path separator in it is refused:
 * the value is chosen by the server, but a header is still not a reason to write `../` into a
 * download.
 */
function filenameOf(response: Response): string | null {
  const header = response.headers.get('content-disposition')
  const match = header === null ? null : /filename="([^"]+)"/.exec(header)
  const name = match?.[1]

  return name === undefined || name.includes('/') || name.includes('\\') ? null : name
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  readonly query?: Readonly<Record<string, QueryValue | readonly QueryValue[]>>
  readonly body?: unknown
  readonly signal?: AbortSignal
  /** Multipart uploads pass a FormData body; the browser must set its own boundary header. */
  readonly formData?: FormData
  /** Return the raw bytes instead of parsed JSON — see the download note below. */
  readonly asBlob?: boolean
}

/**
 * Called when any request comes back 401.
 *
 * A module-level hook rather than a React context because the client is used outside React —
 * in TanStack Query's cache and in route loaders — where a hook cannot reach.
 */
type UnauthenticatedHandler = () => void
let onUnauthenticated: UnauthenticatedHandler | null = null

export function setUnauthenticatedHandler(handler: UnauthenticatedHandler | null): void {
  onUnauthenticated = handler
}

function readCsrfToken(): string | null {
  // document.cookie is a flat string; split rather than regex so a value containing '=' survives.
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim()
    const separator = trimmed.indexOf('=')
    if (separator > 0 && trimmed.slice(0, separator) === CSRF_COOKIE) {
      return decodeURIComponent(trimmed.slice(separator + 1))
    }
  }
  return null
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE}${path}`
  if (!query) return url

  const params = new URLSearchParams()

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue

    if (Array.isArray(value)) {
      for (const entry of value) {
        if (entry !== undefined && entry !== null && entry !== '') params.append(key, String(entry))
      }
    } else {
      params.set(key, String(value))
    }
  }

  const serialized = params.toString()
  return serialized ? `${url}?${serialized}` : url
}

async function toProblem(response: Response): Promise<Problem> {
  const contentType = response.headers.get('content-type') ?? ''

  // A 502 from a proxy is HTML, and a dropped upstream can be empty. Neither should throw here
  // and turn a clear "the gateway is down" into an unhelpful JSON parse error.
  if (!contentType.includes('json')) {
    return { status: response.status, title: response.statusText }
  }

  try {
    return (await response.json()) as Problem
  } catch {
    return { status: response.status, title: response.statusText }
  }
}

/**
 * Paths that must never trigger a refresh attempt.
 *
 * Refreshing in response to a 401 from one of these is either meaningless (a failed sign-in) or
 * an infinite loop (the refresh itself).
 */
const NO_REFRESH = new Set(['/auth/login', '/auth/refresh', '/auth/logout'])

/**
 * The single in-flight refresh, shared by every request that meets a 401 at the same moment.
 *
 * A dashboard fires a dozen queries at once. Without this, an expired access cookie would
 * produce a dozen simultaneous refreshes, each rotating the token out from under the others —
 * and rotation means all but one would fail, signing the admin out anyway.
 */
let refreshInFlight: Promise<boolean> | null = null

function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const headers = new Headers({ Accept: 'application/json' })
      const csrf = readCsrfToken()
      if (csrf) headers.set(CSRF_HEADER, csrf)

      // eslint-disable-next-line no-restricted-syntax -- this IS the one API client (§1.4)
      const response = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST',
        headers,
        credentials: 'include',
        cache: 'no-store',
      })

      return response.ok
    } catch (cause) {
      log.exception('Could not reach the server to refresh the session', cause)

      return false
    } finally {
      // Cleared regardless, so the next expiry starts a fresh attempt rather than replaying
      // the answer to this one.
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return attempt<T>(path, options, true)
}

async function attempt<T>(
  path: string,
  options: RequestOptions,
  mayRefresh: boolean,
): Promise<T> {
  const method = options.method ?? 'GET'
  const headers = new Headers({ Accept: 'application/json' })

  if (UNSAFE.has(method)) {
    const csrf = readCsrfToken()
    if (csrf) {
      headers.set(CSRF_HEADER, csrf)
    } else {
      // Not fatal here — the server refuses it, which is the correct place for that decision.
      // Logged because in practice it means the session cookie was never set.
      log.warn('No CSRF cookie present for an unsafe request', { path, method })
    }
  }

  let body: BodyInit | undefined
  if (options.formData) {
    body = options.formData
  } else if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json')
    body = JSON.stringify(options.body)
  }

  let response: Response
  try {
    // eslint-disable-next-line no-restricted-syntax -- this IS the one API client (§1.4)
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      ...(body !== undefined ? { body } : {}),
      credentials: 'include',
      // The session must never be served from the HTTP cache; a stale 200 would show
      // a signed-out admin the previous admin's data.
      cache: 'no-store',
      ...(options.signal ? { signal: options.signal } : {}),
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause

    log.exception('Request could not reach the server', cause, { path, method })
    throw new NetworkError(cause)
  }

  if (response.status === 401) {
    /*
     * **The access cookie lives fifteen minutes; the refresh cookie lives thirty days.**
     *
     * Without this the panel signed the administrator out every quarter of an hour, mid-task,
     * with a perfectly good refresh cookie sitting in the browser unused. One retry only: if the
     * refresh itself fails the session is genuinely over, and retrying past that is how a client
     * ends up in a loop against a server that has already said no.
     */
    if (mayRefresh && !NO_REFRESH.has(path) && (await refreshSession())) {
      return attempt<T>(path, options, false)
    }

    onUnauthenticated?.()
    throw new ApiError(401, await toProblem(response))
  }

  if (!response.ok) {
    const problem = await toProblem(response)
    log.warn('Request refused', { path, method, status: response.status, code: problem.code })
    throw new ApiError(response.status, problem)
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }

  /*
   * **A file, not a document.** An analytics export is fetched with the session cookie like every
   * other request rather than through a signed public link, because the file can carry pseudonymous
   * rows and, with `includeIdentity`, names — a capability URL for that is a link anybody it is
   * forwarded to can open. So it comes back through this client and the caller turns it into a
   * download itself.
   */
  if (options.asBlob) {
    return { blob: await response.blob(), filename: filenameOf(response) } as T
  }

  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('json')) return undefined as T

  return (await response.json()) as T
}

export const api = {
  /** Fetches a file through the session rather than through a link anybody could forward. */
  download: (path: string) => request<DownloadedFile>(path, { method: 'GET', asBlob: true }),

  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', ...(query ? { query } : {}), ...(signal ? { signal } : {}) }),

  post: <T>(path: string, body?: unknown, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'POST', body, ...(query ? { query } : {}) }),

  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),

  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'DELETE', ...(body !== undefined ? { body } : {}) }),

  /*
   * Uploads take a query as well as the parts. The asset routes read `type`, `name` and
   * `categoryId` from the QUERY STRING, not the body — Fastify cannot validate a multipart body,
   * so everything the server must actually check lives in the query where ajv can reach it.
   */
  upload: <T>(
    path: string,
    formData: FormData,
    query?: RequestOptions['query'],
    method: 'POST' | 'PUT' = 'POST',
  ) => request<T>(path, { method, formData, ...(query ? { query } : {}) }),
}
